// @ts-nocheck
/**
 * HTML Renderer - Generates HTML for products
 */
/** Plain-text value for `data-variant-notice` (HTML attribute escaping only). */
function cmrpEscapeHtmlAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

const CMRP_USED_PRINT_VARIANT_NOTICE =
  'Used materials: might not include supplemental materials like access cards or CDs.';
if (typeof window !== 'undefined') {
  window.CMRP_USED_PRINT_VARIANT_NOTICE = CMRP_USED_PRINT_VARIANT_NOTICE;
  window.CMRP_RENTAL_ONLY_LOG = window.CMRP_RENTAL_ONLY_LOG || [];
  window.CMRP_TRADITIONAL_SKU_LOG = window.CMRP_TRADITIONAL_SKU_LOG || [];
}

class ShopifyHTMLRenderer {
  /**
   * @param {any} variantDetector - Variant detector instance
   * @param {any} priceCalculator - Price calculator instance
   */
  constructor(variantDetector, priceCalculator) {
    this.variantDetector = variantDetector;
    this.priceCalculator = priceCalculator;

    // Expose shared helpers for other modules (e.g. material-interaction-manager)
    window.ProductSystem = window.ProductSystem || {};
    window.ProductSystem.getVariantIsDigitalString =
      this.getVariantIsDigitalString.bind(this);
    window.ProductSystem.getVariantPublisherCode =
      this.getVariantPublisherCode.bind(this);
    window.ProductSystem.courseMaterialsShouldHideDigitalPurchase =
      this.courseMaterialsShouldHideDigitalPurchase.bind(this);
    window.ProductSystem.courseMaterialsShouldHideBlockedAccessSku =
      this.courseMaterialsShouldHideBlockedAccessSku.bind(this);
    window.ProductSystem.getVariantSalesChannelRaw =
      this.getVariantSalesChannelRaw.bind(this);
    window.ProductSystem.salesChannelTokensContainSc =
      this.salesChannelTokensContainSc.bind(this);
    window.ProductSystem.isNoPurchaseAccessSku =
      this.isNoPurchaseAccessSku.bind(this);
    window.ProductSystem.resolveDisplayProductId =
      this.resolveDisplayProductId.bind(this);
    window.ProductSystem.getVariantIsbn = this.getVariantIsbn.bind(this);
    if (!window.ProductSystem.isFollettDiscoverViewPage) {
      window.ProductSystem.isFollettDiscoverViewPage = function () {
        if (window.__follettDiscoverViewFlow === true) return true;
        if (typeof window.location === 'undefined') return false;
        return String(window.location.pathname || '').includes(
          'follett-discover-view',
        );
      };
    }
  }

  /**
   * @param {Product} product - The product object
   * @returns {string} HTML string
   */
  generateFormatButtons(product) {
    // Use the new dynamic approach - get all unique formats
    const uniqueFormats = this.variantDetector.getUniqueFormats(product);
    const formats = [];

    // For each unique format, create a format button
    uniqueFormats.forEach(formatName => {
      const price = this.priceCalculator.getLowestPriceForFormat(
        product,
        formatName
      );

      if (price) {
        formats.push({
          format: formatName.toLowerCase(),
          label: formatName, // Use the actual format name from Shopify
          price: price,
        });
      }
    });

    // Use the smart variant detector logic to determine if format buttons are needed
    const isSimpleProduct = this.variantDetector.isSimpleProduct(product);

    // If this is a simple product (no complex variant structure), no format buttons needed
    if (isSimpleProduct) {
      return ''; // Return empty string - no format buttons for simple products
    }

    return formats
      .map(formatData => this._renderFormatButton(formatData, product))
      .join('');
  }

  /**
   * @param {Product} product - The product object
   * @returns {string} HTML string
   */
  _getPurchaseTypeOrder(typeName) {
    const format = (typeName || '').toLowerCase();
    if (format.includes('digital')) {
      return ['rent', 'buy'];
    }
    return ['rent used', 'rent new', 'buy used', 'buy new'];
  }

  _purchaseTypeSortIndex(option2, order) {
    const lower = (option2 || '').toLowerCase().trim();
    for (let i = 0; i < order.length; i++) {
      if (lower.indexOf(order[i]) !== -1) return i;
    }
    return 999;
  }

  _sortPurchaseTypeKeys(keys, typeName) {
    const order = this._getPurchaseTypeOrder(typeName);
    return keys.slice().sort(
      (a, b) =>
        this._purchaseTypeSortIndex(a, order) -
        this._purchaseTypeSortIndex(b, order)
    );
  }

  _sortVariantsByPrice(variants) {
    return variants.slice().sort((a, b) => {
      const pa = parseFloat(a?.price);
      const pb = parseFloat(b?.price);
      return (isNaN(pa) ? 0 : pa) - (isNaN(pb) ? 0 : pb);
    });
  }

  async generatePurchaseOptions(typeName, product, sectionData) {
    
    if (this.variantDetector.isSimpleProduct(product)) {
        return await this._generateSimpleProductPurchaseOptions(product,sectionData);
    }

    const defaultFormat = this._getDefaultFormat(product);
    
    if (!defaultFormat) {
        return this._renderNoOptionsMessage();
    }

    /** @type {Record<string, any[]>} */
    const variantsByType = {};
    
    if (product.variants) {
        
        product.variants
            .filter(variant =>
                variant.option1?.toLowerCase().includes(typeName.toLowerCase()) &&
                !this.courseMaterialsShouldHideBlockedAccessSku(product, sectionData, variant)
            )
            .forEach(variant => {
                const type = variant.option2 || 'Buy';
                if (!variantsByType[type]) variantsByType[type] = [];
                variantsByType[type].push(variant);
            });
    }

    if (Object.keys(variantsByType).length === 0) {
        return this._renderNoOptionsMessage();
    }
     /*
      comment 
      FMVP-1051 For SKUs with cm_rental.rental_only_sku = "true" then do not display "Buy Used" or "Buy New" 
    */
    let hasRentalOnlySku = false;
    for (const type of Object.keys(variantsByType)) {
      if (variantsByType[type].some(variant => 
        variant.metafields.some(metafield => 
            metafield.namespace === "cm_rental" && 
            (metafield.rental_only_sku === "true" || metafield.rental_only_sku === true)
        )
      )) {
        hasRentalOnlySku = true;
        break;
      }
    }
    
    const isVariantPurchasable = variant =>
      Boolean(variant?.available) ||
      String(variant?.inventory_policy || '').toLowerCase() === 'continue';

    const retObj = await Promise.all(
        this._sortPurchaseTypeKeys(Object.keys(variantsByType), typeName).map(async type => {
          const variants = this._sortVariantsByPrice(variantsByType[type]);
          if(typeName == "Print"){
              if (hasRentalOnlySku) {                
                if (variants[0].option2 == "Buy New" || variants[0].option2 == "Buy Used") {
                  this._logRentalOnlyDisplayRule(product, variants[0]?.option2, {
                    show: false,
                    rule: 'rental-only-sku',
                    reason:
                      'Rental-only SKU (cm_rental.rental_only_sku) — Buy New and Buy Used are not displayed',
                    extra: { hasRentalOnlySku: true },
                  });
                }else{
                  const decision = this._shouldShowRentalOnlyPrintOption(
                    variants[0]?.option2,
                    product,
                    isVariantPurchasable
                  );
                  this._logRentalOnlyDisplayRule(product, variants[0]?.option2, decision);
                  return decision.show && variants
                    ? await this._renderPurchaseOption(type, variants, product, sectionData)
                    : '';
                }                
              }else{
                const decision = this._shouldShowTraditionalPrintOption(
                  variants[0]?.option2,
                  product,
                  isVariantPurchasable
                );
                this._logTraditionalSkuDisplayRule(product, variants[0]?.option2, decision);
                return decision.show && variants
                  ? await this._renderPurchaseOption(type, variants, product, sectionData)
                  : '';
              }
            
          }else{
            return variants
              ? await this._renderPurchaseOption(type, variants, product, sectionData)
              : '';
          }
        })
    );

    const html = retObj.join('');
    if (!html) return this._renderNoOptionsMessage();
    return this._applyPrintSmartPickLabel(typeName, html);
  }

  /**
   * CMS-193 / Smart Pick (CMRP): when Print rent options are shown, mark available Rent Used
   * (fallback available Rent New) with the Smart Pick pill last on the variant card
   * (after title, price, and rental-due — never under the radio).
   * Available cards use class="purchase-type"; OOS use class="purchase-type unavailable".
   * @param {string} typeName
   * @param {string} html
   * @returns {string}
   */
  _applyPrintSmartPickLabel(typeName, html) {
    if (typeName !== 'Print' || typeof html !== 'string' || !html) return html;
    // Same gate as the former rent-and-save banner: only when a print rent row is displayed.
    if (!/data-purchase-type="[^"]*rent/i.test(html)) return html;

    // Prefer available rent-used; else available rent-new. Do not badge unavailable cards.
    const hasAvailableType = pickType =>
      new RegExp(
        `<div class="purchase-type"[^>]*data-purchase-type="${pickType}"`,
        'i'
      ).test(html);

    const pickType = hasAvailableType('rent-used')
      ? 'rent-used'
      : hasAvailableType('rent-new')
        ? 'rent-new'
        : null;
    if (!pickType) return html;

    const badge =
      '<span class="cmrp-smart-pick-badge">' +
      '<svg class="cmrp-smart-pick-badge__icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M2.5 6.2L4.8 8.7L9.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      'Smart Pick</span>';

    const cardOpenRe = new RegExp(
      `(<div class="purchase-type)("[^>]*data-purchase-type="${pickType}"[^>]*>)`,
      'i'
    );
    let next = html.replace(cardOpenRe, '$1 smart-pick$2');

    // Keep rental-due, then append badge last inside a meta stack.
    const metaWithDueRe = new RegExp(
      `(<div class="purchase-type smart-pick"[^>]*>[\\s\\S]*?)(<div class="rental-duration">[\\s\\S]*?<\\/div>)`,
      'i'
    );
    if (metaWithDueRe.test(next)) {
      return next.replace(
        metaWithDueRe,
        `$1<div class="purchase-type-meta">$2${badge}</div>`
      );
    }

    // Fallback if rental-due is missing: badge alone at the end of the card body.
    return next.replace(
      /(<div class="purchase-type smart-pick"[^>]*>[\s\S]*?<div class="purchase-type-header">[\s\S]*?<\/div>)/i,
      `$1\n            <div class="purchase-type-meta purchase-type-meta--badge-only">${badge}</div>`
    );
  }

  /**
   * @param {Product} product
   * @param {string} option2
   * @param {{ show: boolean, rule: string, reason: string, extra?: Record<string, unknown> }} decision
   */
  _logRentalOnlyDisplayRule(product, option2, decision) {
    if (typeof window === 'undefined') return;
    window.CMRP_RENTAL_ONLY_LOG = window.CMRP_RENTAL_ONLY_LOG || [];
    window.CMRP_RENTAL_ONLY_LOG.push({
      ticket: 'TMTST-2858',      
      product: product?.title || product?.handle || product?.id || 'unknown product',
      option: option2,
      rule: decision.rule,
      action: decision.show ? 'SHOW' : 'HIDE',
      reason: decision.reason,
      ...(decision.extra || {}),
      at: new Date().toISOString(),
    });
  }

  /**
   * Rental-only Print: decide whether Rent New / Rent Used should render.
   * Buy New / Buy Used are already hidden for rental-only SKUs; their stock
   * still drives which rental rows appear.
   * @param {string} option2
   * @param {Product} product
   * @param {(variant: any) => boolean} isVariantPurchasable
   * @returns {{ show: boolean, rule: string, reason: string, extra?: Record<string, unknown> }}
   */
  _shouldShowRentalOnlyPrintOption(option2, product, isVariantPurchasable) {
    const printVariant = type =>
      (product.variants || []).find(
        variant => variant.option1 === 'Print' && variant.option2 === type
      );

    const newOnlyFlag =
      product.metafields?.new_book_only_on_efollett_flag ??
      product.metafields?.course_materials?.new_book_only_on_efollett_flag;
    const isNewOnly = newOnlyFlag === true || newOnlyFlag === 'true';
    const isUsedOption = String(option2 || '').includes('Used');
    const isRentNew = option2 === 'Rent New';
    const isRentUsed = option2 === 'Rent Used';

    const decide = (show, rule, reason, extra) => ({
      show,
      rule,
      reason,
      extra,
    });

    // f. New Only: ignore used inventory; show new options only.
    // In-stock state comes from the new variant itself (used stock is ignored).
    if (isNewOnly) {
      return decide(
        !isUsedOption,
        'f',
        isUsedOption
          ? 'New Only flag is on — used format is ignored and not displayed'
          : 'New Only flag is on — new format is displayed; used inventory is ignored',
        { newOnly: true }
      );
    }

    const buyNew = printVariant('Buy New');
    const buyUsed = printVariant('Buy Used');
    const buyNewInStock = isVariantPurchasable(buyNew);
    const buyUsedInStock = isVariantPurchasable(buyUsed);
    const stockExtra = {
      buyNewInStock,
      buyUsedInStock,
      hasBuyNew: Boolean(buyNew),
      hasBuyUsed: Boolean(buyUsed),
    };

    if (buyNew && buyUsed) {
      // a. Both buy options OOS → only Rent New (OOS styling from the variant)
      if (!buyNewInStock && !buyUsedInStock) {
        return decide(
          isRentNew,
          'a',
          isRentNew
            ? 'Both Buy New and Buy Used are OOS — only Rent New is displayed (as OOS)'
            : 'Both Buy New and Buy Used are OOS — Rent Used is not displayed',
          stockExtra
        );
      }
      // b. Both buy options in stock → Rent New and Rent Used
      if (buyNewInStock && buyUsedInStock) {
        return decide(
          isRentNew || isRentUsed,
          'b',
          'Both Buy New and Buy Used are in stock — Rent New and Rent Used are displayed',
          stockExtra
        );
      }
      // c. Only buy used OOS → Rent New only
      if (!buyUsedInStock && buyNewInStock) {
        return decide(
          isRentNew,
          'c',
          isRentNew
            ? 'Only Buy Used is OOS — Rent New is displayed'
            : 'Only Buy Used is OOS — Rent Used is not displayed',
          stockExtra
        );
      }
      // d. Only buy new OOS → Rent Used only
      if (!buyNewInStock && buyUsedInStock) {
        return decide(
          isRentUsed,
          'd',
          isRentUsed
            ? 'Only Buy New is OOS — Rent Used is displayed'
            : 'Only Buy New is OOS — Rent New is not displayed',
          stockExtra
        );
      }
    }

    const rentNew = printVariant('Rent New');
    const rentUsed = printVariant('Rent Used');
    const rentNewPrice = rentNew?.price != null ? Number(rentNew.price) : NaN;
    const rentUsedPrice = rentUsed?.price != null ? Number(rentUsed.price) : NaN;
    // e. Same rental price → show both Rent New and Rent Used
    if (
      Number.isFinite(rentNewPrice) &&
      Number.isFinite(rentUsedPrice) &&
      rentNewPrice === rentUsedPrice &&
      (isRentNew || isRentUsed)
    ) {
      return decide(
        true,
        'e',
        'Rent New and Rent Used have the same price — both are displayed',
        { ...stockExtra, rentNewPrice, rentUsedPrice }
      );
    }

    return decide(
      true,
      'fallback',
      'No display rule matched — variant is shown as fallback',
      { ...stockExtra, rentNewPrice, rentUsedPrice }
    );
  }

  /**
   * @param {Product} product
   * @param {string} option2
   * @param {{ show: boolean, rule: string, reason: string, extra?: Record<string, unknown> }} decision
   */
  _logTraditionalSkuDisplayRule(product, option2, decision) {
    if (typeof window === 'undefined') return;
    window.CMRP_TRADITIONAL_SKU_LOG = window.CMRP_TRADITIONAL_SKU_LOG || [];
    window.CMRP_TRADITIONAL_SKU_LOG.push({
      ticket: 'TMTST-2277',      
      product: product?.title || product?.handle || product?.id || 'unknown product',
      option: option2,
      rule: decision.rule,
      action: decision.show ? 'SHOW' : 'HIDE',
      reason: decision.reason,
      ...(decision.extra || {}),
      at: new Date().toISOString(),
    });
  }

  /**
   * Traditional Print SKU: decide whether Buy/Rent New/Used should render.
   * @param {string} option2
   * @param {Product} product
   * @param {(variant: any) => boolean} isVariantPurchasable
   * @returns {{ show: boolean, rule: string, reason: string, extra?: Record<string, unknown> }}
   */
  _shouldShowTraditionalPrintOption(option2, product, isVariantPurchasable) {
    const printVariant = type =>
      (product.variants || []).find(
        variant => variant.option1 === 'Print' && variant.option2 === type
      );

    const decide = (show, rule, reason, extra) => ({
      show,
      rule,
      reason,
      extra,
    });

    const isBuyNew = option2 === 'Buy New';
    const isBuyUsed = option2 === 'Buy Used';
    const isRentNew = option2 === 'Rent New';
    const isRentUsed = option2 === 'Rent Used';
    const isUsedOption = String(option2 || '').includes('Used');

    const newOnlyFlag =
      product.metafields?.course_materials?.new_book_only_on_efollett_flag ??
      product.metafields?.new_book_only_on_efollett_flag;
    const isNewOnly = newOnlyFlag === true || newOnlyFlag === 'true';

    // 5. New Only: ignore used inventory; always show new (in-stock or OOS)
    if (isNewOnly) {
      return decide(
        !isUsedOption,
        '5',
        isUsedOption
          ? 'New Only flag is on — used format inventory is ignored and is not displayed'
          : 'New Only flag is on — new format is displayed regardless of used inventory',
        {
          newOnly: true,
          flag: 'course_materials.new_book_only_on_efollett_flag',
        }
      );
    }

    const buyNew = printVariant('Buy New');
    const buyUsed = printVariant('Buy Used');
    const buyNewInStock = isVariantPurchasable(buyNew);
    const buyUsedInStock = isVariantPurchasable(buyUsed);
    const stockExtra = {
      buyNewInStock,
      buyUsedInStock,
      hasBuyNew: Boolean(buyNew),
      hasBuyUsed: Boolean(buyUsed),
    };

    if ((isBuyNew || isBuyUsed) && buyNew && buyUsed) {
      // 1. Both Print buy options OOS → only Buy New (as OOS)
      if (!buyNewInStock && !buyUsedInStock) {
        return decide(
          isBuyNew,
          '1',
          isBuyNew
            ? 'Both Print Buy New and Buy Used are OOS — only Buy New is displayed (as OOS)'
            : 'Both Print Buy New and Buy Used are OOS — Buy Used is not displayed',
          stockExtra
        );
      }
      // 2. Both buy options in stock → both displayed
      if (buyNewInStock && buyUsedInStock) {
        return decide(
          true,
          '2',
          'Both Print Buy New and Buy Used are in stock — both are displayed',
          stockExtra
        );
      }
      // 3. Only Buy Used OOS → hide Buy Used, show Buy New
      if (!buyUsedInStock && buyNewInStock) {
        return decide(
          isBuyNew,
          '3',
          isBuyNew
            ? 'Only Buy Used is OOS — Buy New is displayed'
            : 'Only Buy Used is OOS — Buy Used is not displayed',
          stockExtra
        );
      }
      // 4. Only Buy New OOS → hide Buy New, show Buy Used
      if (!buyNewInStock && buyUsedInStock) {
        return decide(
          isBuyUsed,
          '4',
          isBuyUsed
            ? 'Only Buy New is OOS — Buy Used is displayed'
            : 'Only Buy New is OOS — Buy New is not displayed',
          stockExtra
        );
      }
    }

    const rentNew = printVariant('Rent New');
    const rentUsed = printVariant('Rent Used');
    const rentNewInStock = isVariantPurchasable(rentNew);
    const rentUsedInStock = isVariantPurchasable(rentUsed);
    const rentNewPrice = rentNew?.price != null ? Number(rentNew.price) : NaN;
    const rentUsedPrice = rentUsed?.price != null ? Number(rentUsed.price) : NaN;
    const sameRentPrice =
      Number.isFinite(rentNewPrice) &&
      Number.isFinite(rentUsedPrice) &&
      rentNewPrice === rentUsedPrice;
    const rentExtra = {
      ...stockExtra,
      rentNewInStock,
      rentUsedInStock,
      hasRentNew: Boolean(rentNew),
      hasRentUsed: Boolean(rentUsed),
      rentNewPrice,
      rentUsedPrice,
      sameRentPrice,
    };

    // 6a. Same rental price, both in stock → show both
    // 6b. Both OOS → only Rent New (price is not required)
    if (rentNew && rentUsed && (isRentNew || isRentUsed)) {
      const bothInStock = rentNewInStock && rentUsedInStock;
      const bothOos = !rentNewInStock && !rentUsedInStock;
      if (sameRentPrice && bothInStock) {
        return decide(
          true,
          '6a',
          'Rent New and Rent Used have the same price and are both in stock — both are displayed',
          rentExtra
        );
      }
      if (bothOos) {
        return decide(
          isRentNew,
          '6b',
          isRentNew
            ? 'Rent New and Rent Used are both OOS — only Rent New is displayed'
            : 'Rent New and Rent Used are both OOS — Rent Used is not displayed',
          rentExtra
        );
      }
    }

    return decide(
      true,
      'fallback',
      'No traditional display rule matched — variant is shown as fallback',
      rentExtra
    );
  }

  /**
   * @param {any} formatData - Format data
   * @param {Product} product - Product object
   * @returns {string} HTML string
   */
  _renderFormatButton(formatData, product) {
    const productId = product.title.replace(/\s+/g, '-').toLowerCase();
    return `
      <button type="button" class="format-btn" 
              data-format="${formatData.format}" 
              data-product="${productId}">
          <span class="format-label">${formatData.label}</span>
          <span class="format-price">From $${formatData.price}</span>
      </button>
    `;
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Default format
   */
  _getDefaultFormat(product) {
    // Get all unique formats and use the first one as default
    const uniqueFormats = this.variantDetector.getUniqueFormats(product);

    if (uniqueFormats.length > 0) {
      return uniqueFormats[0].toLowerCase();
    }

    return null;
  }

  /**
   * @param {Product} product - Product object
   * @returns {string|null} Default format
   */
  _getFormats(product) {
    // Get all unique formats and use the first one as default
    const uniqueFormats = this.variantDetector.getUniqueFormats(product);

    if (uniqueFormats.length > 0) {
      return uniqueFormats;
    }

    return null;
  }

  async fetchMetaobjects(metaobjectType, queryData = '') {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, queryData);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${encodeURIComponent(
      queryData,
    )}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }

  /**
   * Product id for choice/checkbox wiring on unified CMRP cards (print parent).
   * Merged digital variants may still carry the original CEB parentProductId.
   * @param {any} product - Rendered parent product
   * @param {any} [variant] - Variant row
   * @returns {string}
   */
  resolveDisplayProductId(product, variant) {
    const raw = product?.id ?? variant?.parentProductId;
    if (raw == null || raw === '') return '';
    return String(raw).split('/').pop();
  }

  /**
   * ISBN for a purchase option. Unified-view digital variants keep the CEB
   * product ISBN on `sourceIsbn` when variant metafields do not include one.
   * @param {any} variant
   * @param {any} [product]
   * @returns {string}
   */
  getVariantIsbn(variant, product = null) {
    const pick = (...vals) => {
      for (const val of vals) {
        const text = String(val ?? '').trim();
        if (text) return text;
      }
      return '';
    };
    const fromList = list => {
      if (!Array.isArray(list)) return '';
      const isbnRow = list.find(
        mf => mf && (mf.isbn_ean || (mf.key === 'isbn_ean' && mf.value))
      );
      if (isbnRow) return pick(isbnRow.isbn_ean, isbnRow.value);
      const digitalRow = list.find(
        mf => mf && mf.namespace === 'cm_digital' && (mf.isbn || mf.key === 'isbn')
      );
      if (digitalRow) return pick(digitalRow.isbn, digitalRow.value);
      return '';
    };

    const mf = variant?.metafields;
    const fromVariant = Array.isArray(mf)
      ? fromList(mf)
      : mf && typeof mf === 'object'
        ? pick(
            mf.isbn_ean,
            mf.course_materials?.isbn_ean,
            mf.cm_digital?.isbn,
            mf.isbn
          )
        : '';
    if (fromVariant) return fromVariant;

    const stamped = pick(variant?.sourceIsbn);
    if (stamped) return stamped;

    const productMf = product?.metafields || {};
    return pick(
      productMf.isbn_ean,
      productMf.course_materials?.isbn_ean,
      productMf.isbn,
      productMf.isbn13
    );
  }

  courseMaterialsIncludedSectionMatch(product, sectionData) {
    const sectionId = String(sectionData?.courseInfo?.selectedsectionId ?? '').trim();
    if (!sectionId || !product) return false;
  
    const mf = product?.metafields;
    const courseMaterials = mf?.course_materials;
    const raw =
      courseMaterials?.section_data_mapping ??
      courseMaterials?.section_data_mapping?.value ??
      mf?.['course_materials.section_data_mapping'] ??
      mf?.course_materials_section_data_mapping ??
      mf?.section_data_mapping;
  
    let arr = null;
    try {
      if (raw == null) return false;
      if (typeof raw === 'string') arr = JSON.parse(raw);
      else if (Array.isArray(raw)) arr = raw;
      else if (raw && typeof raw === 'object' && raw.value != null) {
        arr = typeof raw.value === 'string' ? JSON.parse(raw.value) : raw.value;
      } else return false;
    } catch {
      return false;
    }
    if (!Array.isArray(arr) || arr.length === 0) return false;
  
    const entry = arr.find(e => {
      if (!e || typeof e !== 'object') return false;
      const sid = e.SectionID ?? e.section_id ?? e.sectionId;
      return sid != null && String(sid) === String(sectionId);
    });
    if (!entry || typeof entry !== 'object') return false;
  
    const flag =
      entry.IncludEDMaterialFlag ??
      entry.includedMaterialFlag ??
      entry.IncludedMaterialFlag;
    return flag != null && String(flag).toLowerCase() === 'true';
  }
  getVariantSalesChannelRaw(variant) {
    const mf = variant?.metafields;
    if (!mf) return '';
    if (typeof mf === 'object' && !Array.isArray(mf)) {
      if (mf.cm_digital?.sales_channel != null) {
        return String(mf.cm_digital.sales_channel);
      }
      if (mf.custom?.sales_channel != null) {
        return String(mf.custom.sales_channel);
      }
    }
    if (Array.isArray(mf)) {
      const cmDigitalRow = mf.find(
        x =>
          x &&
          x.namespace === 'cm_digital' &&
          (x.key === 'sales_channel' || x.sales_channel != null)
      );
      if (cmDigitalRow?.sales_channel != null) return String(cmDigitalRow.sales_channel);

      const customRow = mf.find(
        x =>
          x &&
          x.namespace === 'custom' &&
          (x.key === 'sales_channel' || x.sales_channel != null)
      );
      if (customRow?.sales_channel != null) return String(customRow.sales_channel);
    }
    return '';
  }
  salesChannelTokensContainSc(raw) {
    return String(raw)
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(Boolean)
      .includes('SC');
  }
  /**
   * Shop metafield custom.noPurchase_AccessSKUs (injected as shopifySettings.noPurchaseAccessSKUs).
   * Returns null when the metafield is missing or empty so callers can no-op.
   * @returns {Set<string> | null}
   */
  getNoPurchaseAccessSkuSet() {
    const raw =
      typeof window !== 'undefined' ? window.shopifySettings?.noPurchaseAccessSKUs : null;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const set = new Set(
      raw.map(s => String(s ?? '').trim()).filter(Boolean)
    );
    return set.size > 0 ? set : null;
  }
  /**
   * @param {unknown} sku
   * @returns {boolean}
   */
  isNoPurchaseAccessSku(sku) {
    const set = this.getNoPurchaseAccessSkuSet();
    if (!set) return false;
    const needle = String(sku ?? '').trim();
    return needle !== '' && set.has(needle);
  }
  /**
   * @param {any} variant
   * @param {any} [product] — optional parent product (unified CMRP: `variant_digital_status` map)
   */
  getVariantIsDigitalString(variant, product = null) {
    const mf = variant?.metafields;
    if (mf) {
      if (Array.isArray(mf)) {
        const hit = mf.find(m => m && m.is_digital != null);
        if (hit) {
          const v = hit.is_digital;
          if (v === true || String(v).toLowerCase() === 'true') return 'true';
        }
      } else if (typeof mf === 'object') {
        if (mf.cm_digital?.is_digital != null) {
          const v = mf.cm_digital.is_digital;
          if (v === true || String(v).toLowerCase() === 'true') return 'true';
        }
        if (mf.is_digital != null) {
          const v = mf.is_digital;
          if (v === true || String(v).toLowerCase() === 'true') return 'true';
        }
      }
    }

    const statusMap = product?.metafields?.custom?.variant_digital_status;
    if (statusMap && variant?.id) {
      const suffix = String(variant.id).split('/').pop();
      const st = statusMap[suffix];
      if (st !== undefined && st !== null && st !== '') {
        if (st === true || String(st).toLowerCase() === 'true') return 'true';
      }
    }
    return '';
  }

  /**
   * @param {any} variant
   * @param {any} [product] — optional parent (merged add-ons may rely on product-level publisher)
   */
  getVariantPublisherCode(variant, product = null) {
    const mf = variant?.metafields;
    if (Array.isArray(mf)) {
      const hit = mf.find(
        m => m && m.publisher_code && m.namespace === 'course_materials',
      );
      if (hit?.publisher_code) return String(hit.publisher_code).trim();
    } else if (mf && typeof mf === 'object') {
      const o =
        mf.course_materials?.publisher_code ||
        mf.cm_digital?.publisher_code ||
        mf.publisher_code;
      if (o != null && String(o).trim() !== '') return String(o).trim();
    }
    const root =
      product?.metafields?.course_materials?.publisher_code ??
      product?.metafields?.publisher_code;
    if (root != null && String(root).trim() !== '') return String(root).trim();
    return '';
  }
  courseMaterialsShouldHideDigitalPurchase(
    product,
    sectionData,
    variant,
    isDigitalStr
  ) {
    const isDig =
      isDigitalStr === true ||
      String(isDigitalStr || '').toLowerCase() === 'true';
    if (!isDig) return false;
    if (!this.courseMaterialsIncludedSectionMatch(product, sectionData)) return false;
    const raw = this.getVariantSalesChannelRaw(variant);
    if (raw === '') return false;
    return !this.salesChannelTokensContainSc(raw);
  }
  /**
   * Access product + SKU in noPurchase_AccessSKUs. No format or sales-channel check.
   * No-op when the shop metafield is missing/empty.
   * @param {any} product
   * @param {any} sectionData
   * @param {any} variant
   * @returns {boolean}
   */
  courseMaterialsShouldHideBlockedAccessSku(product, sectionData, variant) {
    if (!this.courseMaterialsIncludedSectionMatch(product, sectionData)) return false;
    return this.isNoPurchaseAccessSku(variant?.sku);
  }

  /**
   * @param {string} type - Purchase type
   * @param {ProductVariant[]} variants - Variants array
   * @param {Product} product - Product object
   * @returns {string} HTML string
   */
  async _renderPurchaseOption(type, variants, product, sectionData) {
    
    if (!variants || variants.length === 0) return '';

    // Use a Set to track unique IDs
    const uniqueIds = new Set();

    // Filter out duplicates
    const filteredVariants = this._sortVariantsByPrice(
      variants.filter(variant => {
        if (!uniqueIds.has(variant.id)) {
            uniqueIds.add(variant.id); // Add the ID to the Set if it's not already present
            return true; // Keep this variant
        }
        return false; // Discard this variant
      })
    );

    let htmlOutput = '';
    const isVariantPurchasable = variant =>
      Boolean(variant?.available) ||
      String(variant?.inventory_policy || '').toLowerCase() === 'continue';
    for (const variant of filteredVariants) {
      const price = parseFloat(variant.price).toFixed(2);
      const typeId = type.toLowerCase().replace(/\s+/g, '-');
      const isAvailable = isVariantPurchasable(variant);
      const productId = product.title.replace(/\s+/g, '-').toLowerCase();

      const availabilityClass = isAvailable ? '' : ' unavailable';
      const disabledAttr = isAvailable ? '' : ' disabled';

      const variantValueId = variant.id.split("/").pop();
      const sectionId = sectionData?.courseInfo?.selectedsectionId
        ? String(sectionData.courseInfo.selectedsectionId).replace(/[^a-zA-Z0-9-]/g, '_')
        : '';
      const checkboxId = sectionId
        ? `${typeId}-${productId}-${variantValueId}-${sectionId}`
        : `${typeId}-${productId}-${variantValueId}`;

      let typeName = '';

      const mfa = Array.isArray(variant?.metafields) ? variant.metafields : [];
      const isDigitalStr = this.getVariantIsDigitalString(variant, product);
      const publisher_code = this.getVariantPublisherCode(variant, product);

      const bookISBN = this.getVariantIsbn(variant, product);
      const bookEdition =
        mfa.find(metafield => metafield.edition)?.edition ||
        product?.metafields?.edition ||
        product?.metafields?.course_materials?.edition ||
        '';
      const bookCopyright = mfa.find(metafield => metafield.copyright_year)?.copyright_year || '';
      const bookPublisher = mfa.find(metafield => metafield.publisher_name)?.publisher_name || '';
      const isRental = mfa.find(metafield => 
          metafield.is_rental && metafield.namespace === "cm_rental"
      )?.is_rental || '';

      const rental_only_sku = mfa.find(metafield => 
          metafield.rental_only_sku && metafield.namespace === "cm_rental"
      )?.rental_only_sku || '';

      const subscriptionLength = mfa.find(metafield => metafield.subscription)?.subscription || '';

      const taxonomy_dep = mfa.find(metafield => 
          metafield.department && metafield.namespace === "taxonomy"
      )?.department || '';

      const taxonomy_sub_dep = mfa.find(metafield => 
          metafield.sub_department && metafield.namespace === "taxonomy"
      )?.sub_department || '';

      const taxonomy_class = mfa.find(metafield => 
          metafield.class && metafield.namespace === "taxonomy"
      )?.class || '';

      const taxonomy_sub_class = mfa.find(metafield => 
          metafield.sub_class && metafield.namespace === "taxonomy"
      )?.sub_class || '';

      const hidePurchase =
        this.courseMaterialsShouldHideBlockedAccessSku(product, sectionData, variant) ||
        this.courseMaterialsShouldHideDigitalPurchase(
          product,
          sectionData,
          variant,
          isDigitalStr
        );

      const discoverViewNoSelection =
        window.ProductSystem?.isFollettDiscoverViewPage?.() === true;
      // OOS rows still show the checkmark UI; the input is disabled (see disabledAttr).
      const showPurchaseCheckbox =
        !discoverViewNoSelection && !hidePurchase;
      const showPurchasePrice = !hidePurchase;

      let rental_end_date = '';
      let formattedDate = '';
      if(isRental == "true" && isDigitalStr !== "true"){
        const queryData = "fields.term_id:'"+sectionData.courseInfo.termId+"' fields.disabled_flag:false fields.is_term_open_flag:true"
        const filteredTerms = await this.fetchMetaobjects('ddcs_term', queryData);
        rental_end_date = filteredTerms[0]?.rental_end_date ? filteredTerms[0].rental_end_date : '';
        if(rental_end_date != ''){
          const dateParts = rental_end_date.split('-');
          formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
        }
      }
      if(typeName == ''){
        if(variant.option2 == ''){
          typeName = variant.option1
        }else{
          typeName = variant.option2
        }
      }

      const typeNameLower = String(typeName || '').toLowerCase();
      const hasDigitalRentDuration =
        isDigitalStr === 'true' &&
        typeNameLower.includes('rent') &&
        subscriptionLength !== '' &&
        subscriptionLength != null &&
        Number(subscriptionLength) > 0;
      const displayTypeName = hasDigitalRentDuration
        ? `${subscriptionLength} Day Rental`
        : String(typeName);

      // Print BUY USED / RENT USED: notice text is stored on the row; shown above
      // the variants grid whenever any used-print option exists (not selection-tied).
      const isPrintFormat = variant.option1?.toLowerCase() === 'print';
      const typeLower = type.toLowerCase();
      const printVariantNoticeEligible =
        isPrintFormat &&
        (typeLower.includes('buy used') ||
          typeLower.includes('rent used') ||
          (typeLower.includes('used') &&
            (typeLower.includes('buy') || typeLower.includes('rent'))));
      const variantNoticeAttr = printVariantNoticeEligible
        ? ` data-variant-notice="${cmrpEscapeHtmlAttr(CMRP_USED_PRINT_VARIANT_NOTICE)}"`
        : '';

      const hideOosNotifyOnCmrp =
        typeof document !== 'undefined' &&
        document.body?.classList?.contains('course-materials-page') === true;
      const oosNotifyAttrs =
        !isAvailable && !hideOosNotifyOnCmrp
          ? `onclick='event.stopPropagation(); window.handleNotifyMe(${JSON.stringify(variant.id)}, ${JSON.stringify(product.title)}, ${JSON.stringify(typeName)});'`
          : '';

      const requirementsButtonHtml =
        isDigitalStr === 'true'
          ? `<div class="" data-format-specific="digital">
                  <button type="button" class="requirements-link" data-cmrp-req-payload="${encodeURIComponent(JSON.stringify({ productTitle: product.title, variant }))}" onclick="window.cmrpOpenRequirementsFromButton(this)">
                      Requirements
                  </button>
              </div>`
          : '';

      const oosFootHtml = !isAvailable
        ? hideOosNotifyOnCmrp
          ? `<div class="purchase-type__oos-tooltip" role="tooltip">This item is out of stock.</div>
          <div class="purchase-price out-of-stock-overlay"> Out of Stock </div>`
          : `
          <div class="purchase-type__oos-tooltip" role="tooltip">This item is out of stock.</div>
          <div class="purchase-price out-of-stock-overlay"> Out of Stock </div>
          <div class="purchase-type__oos-foot">
            <button type="button" class="purchase-type__notify-btn" ${oosNotifyAttrs}>
              <svg class="purchase-type__notify-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
                <path d="M3 7l9 6 9-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Notify Me</span>
            </button>
          </div>`
        : '';

      htmlOutput += `
      <div class="purchase-type${availabilityClass}"${variantNoticeAttr}
          data-purchase-type="${typeId}" 
          data-variant-id="${variant.id}"
          onclick="window.togglePurchaseOption(this, event)">
          
          <div class="purchase-type-header">
              <div class="purchase-type-label-container">
                  <span class="purchase-type-label">
                      ${displayTypeName}
                  </span>
              </div>
              
              <div class="purchase-price">$${price}</div>

              ${
                showPurchaseCheckbox
                  ? `<div class="purchase-checkbox-container" onclick="event.stopPropagation()">
                  <input type="checkbox" 
                        id="${checkboxId}" 
                        class="purchase-checkbox" 
                        aria-label="${cmrpEscapeHtmlAttr([product.title, isDigitalStr === 'true' ? 'Digital' : 'Physical', displayTypeName, `$${price}`].filter(Boolean).join(', '))}"
                        data-format="${variant.option1}"
                        data-type="${typeName}"
                        data-product-id="${this.resolveDisplayProductId(product, variant)}"
                        data-purchase-type="${typeId}"
                        data-variant-id="${variant.id}"
                        data-variant-price="${price}"
                        data-section-id="${sectionData.courseInfo.selectedsectionId}"
                        data-is-digital="${isDigitalStr === 'true' ? 'true' : 'false'}"
                        data-book-isbn="${bookISBN}"
                        data-book-edition="${bookEdition}"
                        data-book-copyright="${bookCopyright}"
                        data-book-publisher="${bookPublisher}"
                        data-publisher-code="${ publisher_code }"
                        data-book-rent-due-date="${formattedDate}"
                        data-product-title="${cmrpEscapeHtmlAttr(product.title)}"
                        data-department-taxonomy="${taxonomy_dep}"
                        data-sub-department="${taxonomy_sub_dep}"
                        data-class="${taxonomy_class}"
                        data-sub-class="${taxonomy_sub_class}"
                        data-rental-only-sku="${rental_only_sku}"
                        data-subscription-length="${subscriptionLength}"
                        onchange="window.handlePurchaseSelection(this)"${disabledAttr}>
                  <label for="${checkboxId}" class="checkbox-label" aria-hidden="true"></label>
              </div>`
                  : ''
              }
              
          </div>
          
          
            ${requirementsButtonHtml}
            ${formattedDate != '' ? `
              <div class="rental-duration"><span class="rental-duration-label">Rental Due</span> <span class="days-text">${formattedDate}</span></div>
            `  : '' }
            ${oosFootHtml}
        </div>
      `;
    }
    return htmlOutput;
  }

  /**
   * Generate purchase options for simple products (no complex variants)
   * @param {Product} product - Product object
   * @returns {string} HTML string
   */
  async _generateSimpleProductPurchaseOptions(product, sectionData) {
    if (!product.variants || product.variants.length === 0) {
      return this._renderNoOptionsMessage();
    }

    
    const availableVariants = product.variants;

    if (availableVariants.length === 0) {
      return this._renderNoOptionsMessage();
    }

    
    /** @type {Record<string, any[]>} */
    const variantsByType = {};

    availableVariants.forEach(variant => {
      if (this.courseMaterialsShouldHideBlockedAccessSku(product, sectionData, variant)) {
        return;
      }
      const type = variant.option2 || 'Buy';
      if (!variantsByType[type]) variantsByType[type] = [];
      variantsByType[type].push(variant);
    });

    const formatHint =
      availableVariants[0]?.option1 ||
      this._getDefaultFormat(product) ||
      'Print';

    const purchaseOptions = await Promise.all(
      this._sortPurchaseTypeKeys(Object.keys(variantsByType), formatHint).map(async type => {
        const variants = this._sortVariantsByPrice(variantsByType[type]);
        return variants
          ? await this._renderPurchaseOption(type, variants, product, sectionData)
          : '';
      })
    );
    const html = purchaseOptions.join('');
    if (!html) return this._renderNoOptionsMessage();
    const smartPickFormat = String(formatHint).toLowerCase().includes('print')
      ? 'Print'
      : formatHint;
    return this._applyPrintSmartPickLabel(smartPickFormat, html);
  }

  /**
   * @returns {string} HTML string
   */
  _renderNoOptionsMessage() {
    return `
      <div class="no-options-message" style="text-align: center; padding: 20px; color: #666;">
          <p>No purchase options available for this format, yet</p>
          <p style="font-size: 0.875rem; margin-top: 8px;">Please check back in later.</p>
      </div>
    `;
  }
}

window.ProductSystem = window.ProductSystem || {};
window.ProductSystem.ShopifyHTMLRenderer = ShopifyHTMLRenderer;